/**
 * Export Gescom — styles et petits composants partagés par la liste des
 * sessions, le détail d'une session et le flux d'export.
 */

export const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)',
  color: '#fff', border: 'none', borderRadius: 'var(--radius-pill)', padding: '8px 16px',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};
export const secondaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
  color: 'var(--text)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-pill)',
  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
};
export const dangerBtn = { ...secondaryBtn, color: 'var(--danger)', borderColor: 'var(--danger)' };
export const inputStyle = {
  width: '100%', padding: '8px 10px', background: 'var(--glass)', color: 'var(--text)',
  border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', fontSize: 13,
  boxSizing: 'border-box',
};
export const card = {
  marginTop: 20, background: 'var(--card-bg)', border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)', padding: '20px 24px', boxShadow: 'var(--shadow-card)',
};
export const sectionTitle = { margin: 0, fontSize: 15, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 };
export const thStyle = { padding: '6px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 };
export const tdStyle = { padding: '6px 8px', color: 'var(--text)' };

export const pill = (active) => ({
  padding: '6px 14px', borderRadius: 999, border: '1px solid',
  borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
  background: active ? 'var(--accent)' : 'transparent',
  color: active ? '#fff' : 'var(--text-muted)', cursor: 'pointer', fontSize: 13,
});

export const TERMINAL = new Set(['SUCCESS', 'PARTIAL', 'FAILED']);

export function personLabel(p) {
  if (!p) return '';
  return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email || p.id;
}

export function fmtDay(d) {
  if (!d) return '—';
  try {
    return new Date(`${String(d).slice(0, 10)}T00:00:00Z`).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
    });
  } catch { return String(d); }
}

export function fmtDateTime(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(d); }
}

export function Counter({ label, value, color, active, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      style={{
        background: 'var(--glass)', textAlign: 'left',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--glass-border)'}`,
        borderRadius: 'var(--radius-sm)', padding: '10px 16px', minWidth: 110,
        cursor: clickable ? 'pointer' : 'default', color: 'inherit', font: 'inherit',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
    </button>
  );
}

export function StatusBadge({ status }) {
  const map = {
    PENDING: { c: 'var(--text-muted)', t: 'En attente' },
    RUNNING: { c: 'var(--accent)', t: 'En cours' },
    SUCCESS: { c: 'var(--success)', t: 'Succès' },
    PARTIAL: { c: 'var(--warning)', t: 'Partiel' },
    FAILED: { c: 'var(--danger)', t: 'Échec' },
  };
  const m = map[status] ?? map.PENDING;
  return <span style={{ color: m.c, fontWeight: 600 }}>{m.t}</span>;
}

/** Statut calculé d'une ligne de session. */
export const LINE_STATUS = {
  pushed: { label: 'Poussée', color: 'var(--success)' },
  pending: { label: 'À pousser', color: 'var(--accent)' },
  error: { label: 'En erreur', color: 'var(--danger)' },
  blocked: { label: 'Bloquée', color: 'var(--warning)' },
};

export function LineStatusChip({ status }) {
  const m = LINE_STATUS[status] ?? LINE_STATUS.pending;
  return (
    <span
      style={{
        display: 'inline-block', fontSize: 11.5, padding: '1px 8px', borderRadius: 999,
        border: `1px solid ${m.color}`, color: m.color, whiteSpace: 'nowrap',
      }}
    >
      {m.label}
    </span>
  );
}

/** Barre de progression d'un job (suivi par polling ou SSE). */
export function JobProgress({ job }) {
  if (!job) return null;
  const total = job.totals?.total ?? 0;
  const processed = job.processed ?? ((job.totals?.success ?? 0) + (job.totals?.failed ?? 0));
  const running = !TERMINAL.has(job.status);
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : (running ? 0 : 100);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 4 }}>
        <span>{processed} / {total || '?'} traitées</span>
        <span>{pct}%</span>
      </div>
      <div style={{ height: 8, background: 'var(--glass)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
        <div
          style={{
            height: '100%', width: `${pct}%`,
            background: job.status === 'FAILED' ? 'var(--danger)' : job.status === 'PARTIAL' ? 'var(--warning)' : 'var(--accent)',
            transition: 'width 200ms',
          }}
        />
      </div>
      <div style={{ marginTop: 6, display: 'flex', gap: 14, fontSize: 12.5, flexWrap: 'wrap' }}>
        <StatusBadge status={job.status} />
        <span style={{ color: 'var(--success)' }}>{job.totals?.success ?? 0} ok</span>
        <span style={{ color: 'var(--danger)' }}>{job.totals?.failed ?? 0} échec</span>
        <span style={{ color: 'var(--text-muted)' }}>{job.totals?.skipped ?? 0} ignoré</span>
      </div>
    </div>
  );
}

export const SPIN_CSS = '@keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}';
